import {
  IAppService,
  IStateConfig,
  IFxpAppContext,
  IServiceEndPoints,
  IRouteInfo,
  IPartnerBundle,
  IAppServiceWithSharedComponent,
  ISharedComponents,
  ISharedComponentConfig,
  ComponentFramework,
} from '@fxp/fxpservices';
import { appSettings } from './environments/common/appsettingsGenerator';
import { PartnerAppRegistrationService } from '@fxp/fxpservices';

export class RMAvailabilityReactApp_routes
  implements IAppService, IAppServiceWithSharedComponent
{
  getSharedComponentsInfo(): ISharedComponents {
    const MonthlyCalendarSharedComp: ISharedComponentConfig = {
      componentDescription:
        'This component renders react availability calendar.',
      componentInputs: [
        ['dateMonth', 'month to view'],
        ['eventViewType', "One of 'Assignment', 'Outlook'"],
      ],
      componentOutputs: [
        ['onSelectEvent', 'generic event that determines selected event'],
      ],
      exposeAsExternalComponents: false,
      componentFramework: ComponentFramework.React,
      componentName: 'rmavailabilityreactapp-mycalendar',
    };
    const AvailabilitySharedComp: ISharedComponentConfig = {
      componentDescription:
        'This component renders react availability edit view.',
      exposeAsExternalComponents: false,
      componentFramework: ComponentFramework.React,
      componentName: 'rmavailabilityreactapp-availabilityviewcomponent',
    };
    const YearlyCalendarSharedComp: ISharedComponentConfig = {
      componentDescription:
        'This component renders a yearly calendar with events and multiple view modes.',
      componentInputs: [
        ['selectedDate', 'Currently selected date'],
        ['events', 'Array of calendar events'],
        ['config', 'Calendar configuration options'],
        ['popupSettings', 'Event popup settings'],
      ],
      componentOutputs: [
        ['onDateChange', 'Callback when date changes'],
        ['onDaySelect', 'Callback when a day is selected'],
        ['onEventClick', 'Callback when an event is clicked'],
        ['onModeChange', 'Callback when view mode changes'],
      ],
      exposeAsExternalComponents: false,
      componentFramework: ComponentFramework.React,
      componentName: 'rmavailabilityreactapp-yearlycalendar',
    };

    return {
      appName: 'rmavailabilityreactapp',
      components: [MonthlyCalendarSharedComp, AvailabilitySharedComp, YearlyCalendarSharedComp],
      disableSharing: false,
      sharedBundles: ['rmavailabilityreactapp-bundle'],
    };
  }
  getRoutes(fxpContext: IFxpAppContext): IRouteInfo {
    const CESMonthlyCalendar: IStateConfig = {
      name: 'mycalendar',
      url: '/my-calendar',
      componentFramework: 'React',
      data: {
        headerName: 'My Calendar',
        breadcrumbText: 'My Calendar Component',
        pageTitle: 'My Calendar Page',
      },
    };
    const CESEditAvailability: IStateConfig = {
      name: 'editavailability',
      url: '/edit-availability',
      componentFramework: 'React',
      data: {
        headerName: 'Edit Availability',
        breadcrumbText: 'Edit Availability',
        pageTitle: 'Edit Availability Page',
      },
    };
    const YearlyCalendarRoute: IStateConfig = {
      name: 'yearlycalendar',
      url: '/yearlycalendar',
      componentFramework: 'React',
      data: {
        headerName: 'Yearly Calendar',
        breadcrumbText: 'Yearly Calendar',
        pageTitle: 'Yearly Calendar Page',
      },
    };
    const YearlyCalendarResourceRoute: IStateConfig = {
      name: 'yearlycalendar-resource',
      url: '/yearlycalendar/:alias/:role',
      componentFramework: 'React',
      data: {
        headerName: 'Yearly Calendar',
        breadcrumbText: 'Resource Calendar',
        pageTitle: 'Yearly Calendar Page',
      },
    };

    /*
     * Three-segment Functional Manager shape.
     *
     * GRM's `grm-resourcedashboardfm` carries a third `managerAlias` segment. Without a matching
     * React state, ui-router silently dropped it, so the FM entry point lost its context with no
     * error. This route accepts it and `resolveCalendarRoute` captures it.
     *
     * NOTE: the page does not yet USE managerAlias - legacy renders a manager's-team resource
     * selector from it (calendar.controller.js:3040-3051), which is not ported. The route resolves
     * the correct resource; the selector remains a known gap.
     */
    const YearlyCalendarFmRoute: IStateConfig = {
      name: 'yearlycalendar-fm',
      url: '/yearlycalendar/:alias/:role/:managerAlias',
      componentFramework: 'React',
      data: {
        headerName: 'Yearly Calendar',
        breadcrumbText: 'Resource Calendar',
        pageTitle: 'Yearly Calendar Page',
      },
    };

    const FMDashboard: IStateConfig = {
      name: 'fmdashboard',
      url: '/fm-dashboard?manager',
      componentFramework: 'React',
      data: {
        headerName: 'FM Dashboard',
        breadcrumbText: 'FM Dashboard',
        pageTitle: 'FM Dashboard',
      },
      // Parity with legacy `grm-fmdashboard`, whose gate lives in `grm.config.<env>.json:519-525`
      // (NOT in FxpUIConfiguration_*.json, which only declares url/template + requireADLogin):
      //   ProjectAssignment: Read_Team  AND  NonProjectAssignment: Write_Team
      //
      // Two things FXP does differently from GRM's own authorizationService, both load-bearing:
      //  1. `Resource` must be tenant-qualified. FxpAuthorizationService.checkResourcePermissionFunc
      //     does `item.Resource.split('.')` and looks up `tenantClaims[parts[0]]`, so a bare
      //     'ProjectAssignment' resolves no tenant and silently denies everyone.
      //  2. Permission matching is EXACT (`permissions.includes(p)`). FXP has no knowledge of GRM's
      //     PermissionMappingDictionary (`grm.config.*.json:178-184`) where Read_All ⊇ Read_Team and
      //     Write_All ⊇ Write_Team. Users legitimately holding the *_All superset (verified in UAT)
      //     would be denied by a Read_Team-only rule, so the superset is listed explicitly with
      //     AllPermissionsMandatory:false ("any of these").
      authorizationRules: {
        AllowedPermissions: {
          AuthorizeRolesAndResources: false,
          ResourcePermissions: {
            AllResourcesMandatory: true,
            Value: [
              {
                Resource: 'PS.ProjectAssignment',
                Permissions: {
                  AllPermissionsMandatory: false,
                  Value: ['Read_Team', 'Read_All'],
                },
              },
              {
                Resource: 'PS.NonProjectAssignment',
                Permissions: {
                  AllPermissionsMandatory: false,
                  Value: ['Write_Team', 'Write_All'],
                },
              },
            ],
          },
        },
      },
    };
    const FMPendingEvents: IStateConfig = {
      name: 'fmpendingevents',
      url: '/fm-pending-events',
      componentFramework: 'React',
      data: {
        headerName: 'Manage Events',
        breadcrumbText: 'Manage Events',
        pageTitle: 'FM Pending Events',
      },
      // Parity with legacy `grm-fmpendingevents` (`grm.config.<env>.json:526-529`): only
      // NonProjectAssignment: Write_Team. This route approves/rejects OOF requests, so it must stay
      // gated. See the FMDashboard note above for why the resource is tenant-qualified and why the
      // Write_All superset is listed explicitly.
      authorizationRules: {
        AllowedPermissions: {
          AuthorizeRolesAndResources: false,
          ResourcePermissions: {
            AllResourcesMandatory: true,
            Value: [
              {
                Resource: 'PS.NonProjectAssignment',
                Permissions: {
                  AllPermissionsMandatory: false,
                  Value: ['Write_Team', 'Write_All'],
                },
              },
            ],
          },
        },
      },
    };

    const routeInfo: IRouteInfo = {
      applicationName: 'rmavailabilityreactapp',
      sharedBundles: ['rmavailabilityreactapp-bundle'],
      routes: [
        CESMonthlyCalendar,
        CESEditAvailability,
        YearlyCalendarRoute,
        YearlyCalendarResourceRoute,
        YearlyCalendarFmRoute,
        FMDashboard,
        FMPendingEvents,
      ],
    };

    return routeInfo;
  }

  getServiceEndPoints(): Array<IServiceEndPoints> {
    return appSettings().serviceEndPoints;
  }

  getBundles(): IPartnerBundle[] {
    const baseUrl = appSettings().cdnBaseUrl;
    const bundle: IPartnerBundle = {
      name: 'rmavailabilityreactapp-bundle',
      files: [
        `${baseUrl}/vendor.bundle.js`,
        `${baseUrl}/styles.bundle.js`,
        `${baseUrl}/main.bundle.js`,
      ],
      sequentialLoading: true,
    };

    return [bundle];
  }
}

PartnerAppRegistrationService.registerLazyLoadedApp(RMAvailabilityReactApp_routes);
